'use client';

import { Flex, useSteps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePathname } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { render } from 'storyblok-rich-text-react-renderer';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import CustomButton from '@/common/CustomButton/CustomButton';
import CustomErrorMessage from '@/common/CustomErrorMessage/CustomErrorMessage';
import CustomToast from '@/common/CustomToast/CustomToast';
import CustomStoryblokForm from '@/common/storyblok/CustomStoryblokForm/CustomStoryblokForm';
import SuccessPage from '@/components/Webform/pages/SuccessPage/SuccessPage';
import SummaryPage from '@/components/Webform/pages/SummaryPage/SummaryPage';
import envConfig from '@/env.config';
import { Constants, basePath } from '@/lib/constants';
import { WebformPage } from '@/lib/enums';
import {
  getUserProfile,
  sendWebform,
  updateName,
  updateProfile,
} from '@/lib/lib';
import { useAppStore } from '@/stores/AppStore';
import { useUserInfoStore } from '@/stores/UserInfoStore';
import { useWebformStore } from '@/stores/WebformStore';
import { convertStringToClassName } from '@/utils/convertStringToClassName';
import { scrollToElementWithOffset } from '@/utils/scrollToElementWithOffset';
import { scrollToWebformHeader } from '@/utils/scrollToWebformHeader';
import {
  WebformDefaultFormValues,
  WebformTypeEnum,
  generateFormDetailsSchema,
} from '@/validations/Webform/webformSchema';
import './Webform.scss';

interface WebformProps {
  storyblokFormComponents?: any;
}

//////////////////////////////////
// Helper Functions
//////////////////////////////////

//////////////////////////////////
// 1) useFormData
//////////////////////////////////

const useFormData = (storyblokWebformComponents: any) => {
  const pathname = usePathname();
  const isStandaloneForm = usePathname().includes('/forms/');

  /////////////////////////////
  // A) Set existing profile
  /////////////////////////////

  const setWebformConfigValues = (setValue: any) => {
    const formName = storyblokWebformComponents.Name;

    /////////////////////////////
    // Create formNameSlug
    /////////////////////////////

    const formNameSlug = isStandaloneForm
      ? pathname.split('/').filter(Boolean).pop()
      : convertStringToClassName(formName);

    /////////////////////////////
    // Set formConfig values
    /////////////////////////////

    const formConfig = {
      formName: formName,
      formNameSlug: formNameSlug,
    };

    Object.entries(formConfig).forEach(([key, value]) => {
      setValue(`formConfig.${key}`, value);
    });
  };

  /////////////////////////////
  // B) Set existing profile
  /////////////////////////////

  const setExistingProfile = async (
    setValue: any,
    formFields: typeof WebformDefaultFormValues
  ) => {
    try {
      const existingUserProfile = await getUserProfile();
      const { formDetails } = formFields;

      // Iterate over formDetails fields and set the value if it exists
      Object.keys(formDetails).forEach(field => {
        if (existingUserProfile[field] !== undefined) {
          // Set the value only if the field exists in the user profile
          setValue(`formDetails.${field}`, existingUserProfile[field]);
        }
      });

      // Conditionally set contactDetails if contactDetailsPage exists
      // Iterate over existingUserProfile to populate contactDetails with fields not in formDetails
      if (existingUserProfile && 'contactDetails' in formFields) {
        Object.keys(existingUserProfile).forEach(field => {
          if (!formDetails[field]) {
            setValue(`contactDetails.${field}`, existingUserProfile[field]);
          }
        });
      }
    } catch (error) {
      console.error('Failed to pre-fill user profile:', error);
    }
  };

  return { setWebformConfigValues, setExistingProfile };
};

/////////////////////////////
// 2) Extract Page Components
/////////////////////////////

const extractPageComponents = (storyblokWebformComponents: any) => {
  const getPageComponents = (pageName: string) =>
    storyblokWebformComponents.FormLayout.filter(
      (formComponent: any) => formComponent.PageName === pageName
    );

  return {
    formPage: getPageComponents('Form Page'),
    contactDetailsPage: getPageComponents('Contact Details Page'),
    summaryPage: getPageComponents('Summary Page'),
    successPage: getPageComponents('Success Page'),
  };
};

/////////////////////////////
// 3) Extract Field Names and Labels
/////////////////////////////

const extractFieldNamesAndLabels = (components: any[]): any => {
  return components
    .filter(component => component.Name) // Ensure the component has a Name
    .flatMap(component => {
      // Skip 'Title and Content' fields
      if (component.Name === 'Title And Content') {
        return;
      }

      // Recursively extract fields from the Input Group and merge with the rest
      if (component.Name === 'Input Group') {
        return extractFieldNamesAndLabels(component.InputGroup);
      }

      const { Name: formFieldName, Label: componentLabel } = component;

      const componentLabelText = componentLabel?.content
        ? componentLabel.content[0]?.content
            .map((item: any) => item.text)
            .join('')
        : componentLabel;

      return {
        name: formFieldName,
        label: componentLabelText ?? '',
      };
    });
};

/////////////////////////////
// Extract updated profile data
/////////////////////////////

const extractUpdatedProfileData = (formDetails: any, contactDetails: any) => {
  const updatedProfileData: { [key: string]: any } = {};

  Constants.Webform.ContactDetailsFields.forEach(field => {
    // Set profile data from Contact Details page first
    if (contactDetails[field] !== undefined) {
      updatedProfileData[field] = contactDetails[field];
    }

    // Set profile data from Form Details page next
    // Overwrite existing contact details values in updatedProfileData if any
    if (formDetails[field] !== undefined) {
      updatedProfileData[field] = formDetails[field];
    }
  });

  return updatedProfileData;
};

/////////////////////////////
// Update user's name if there are changes
/////////////////////////////

const updateNameIfChanged = async (
  currentName: string,
  firstName: string,
  lastName: string
) => {
  const newName = `${firstName} ${lastName}`;
  if (currentName !== newName) {
    await updateName(firstName, lastName);
  }
};

///////////////////////////////////////////////////////////////////

//////////////////////////////////
// Webform Component
//////////////////////////////////

const Webform = ({ storyblokFormComponents }: WebformProps) => {
  //////////////////////////////
  // App Store
  //////////////////////////////

  const [setShowLoginModal, isNewDesign] = useAppStore(
    useShallow(state => [state.setShowLoginModal, state.isNewDesign])
  );

  //////////////////////////////
  // User Info Store
  //////////////////////////////

  const [isLoggedInWithName, userInfo] = useUserInfoStore(
    useShallow(state => [state.isLoggedInWithName, state.userInfo])
  );

  //////////////////////////////
  // Webform Store
  //////////////////////////////

  const [
    webformToDisplay,
    webformPage,
    setWebformPage,
    showErrorToast,
    setShowErrorToast,
    setRequestId,
  ] = useWebformStore(
    useShallow(state => [
      state.webformToDisplay,
      state.webformPage,
      state.setWebformPage,
      state.showErrorToast,
      state.setShowErrorToast,
      state.setRequestId,
    ])
  );

  //////////////////////////////
  // useState for mergedFormSchema
  //////////////////////////////

  const [mergedFormSchema, setMergedFormSchema] = useState<any>();

  ////////////////////////////////////////
  // React Hook Form (Webform)
  ////////////////////////////////////////

  const webformMethods = useForm({
    resolver: mergedFormSchema ? zodResolver(mergedFormSchema) : undefined,
    defaultValues: {
      ...WebformDefaultFormValues,
      formType: WebformTypeEnum.FormDetails,
    },
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const {
    formState: { isSubmitting, errors },
    getValues,
    setValue,
    handleSubmit,
    trigger,
    watch,
  } = webformMethods;

  const storyblokWebformComponents = webformToDisplay?.FormLayout
    ? webformToDisplay
    : storyblokFormComponents;

  const { formPage, contactDetailsPage, summaryPage, successPage } =
    extractPageComponents(storyblokWebformComponents);

  const formConfig = useFormData(storyblokWebformComponents);

  //////////////////////////////
  // Constants
  //////////////////////////////

  const isStandaloneForm = usePathname().includes('/forms/');
  const hasContactDetailsPage = contactDetailsPage.length > 0;
  const hasSummaryPage = summaryPage.length > 0;

  const isPage = (page: WebformPage) => webformPage === page;

  const isFormPage = isPage(WebformPage.FormDetails);
  const isContactDetailsPage = isPage(WebformPage.ContactDetails);
  const isSummaryPage = isPage(WebformPage.SummaryPage);
  const isSuccessPage = isPage(WebformPage.SuccessPage);

  const isSubmitPage =
    isSummaryPage ||
    (!hasSummaryPage &&
      (isContactDetailsPage || (isFormPage && !hasContactDetailsPage)));

  ///////////////////////////////////////////////
  // useEffect
  // - Save storyblok form field names and labels to be used during report generation
  ///////////////////////////////////////////////

  const formPageFieldNamesAndLabels = extractFieldNamesAndLabels(
    formPage[0].PageLayout
  );

  const contactDetailsPageFieldNamesAndLabels =
    hasContactDetailsPage &&
    extractFieldNamesAndLabels(contactDetailsPage[0].PageLayout);

  useEffect(() => {
    const saveStoryblokFormFieldNamesAndLabels = async () => {
      setValue(
        'formConfig.formPageFieldNamesAndLabels',
        formPageFieldNamesAndLabels
      );

      if (hasContactDetailsPage) {
        setValue(
          'formConfig.contactDetailsPageFieldNamesAndLabels',
          contactDetailsPageFieldNamesAndLabels
        );
      }
    };

    saveStoryblokFormFieldNamesAndLabels();
  }, []);

  ///////////////////////////////////////////////
  // useEffect
  // - Set Webform config
  ///////////////////////////////////////////////

  useEffect(() => {
    formConfig.setWebformConfigValues(setValue);

    // Adobe Analytics - Form Initiation
    // window.digitalData = window.digitalData || {};
    // window.digitalData.events = window.digitalData.events || {};
    // window.digitalData.page = window.digitalData.page || {};
    //
    // window.digitalData.events.eventName = 'formInitiation';
    // window.digitalData.page.formInfo = {
    //   formType: 'Nav Webform',
    //   formName: storyblokWebformComponents.Name,
    //   formPosition: 'form start', // Triggers event11
    // };
    //
    // window._satellite?.track(window.digitalData.events.eventName);
  }, []);

  ///////////////////////////////////////////////
  // useEffect
  // - Set Connection Request Values (Partner, Programme)
  ///////////////////////////////////////////////

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObject = Object.fromEntries(urlParams.entries());

      Object.keys(paramsObject).forEach(key => {
        setValue(`formDetails.${key}`, paramsObject[key]);
      });
    }
  }, []);

  ///////////////////////////////////////////////
  // useEffect
  // - Pre-fill existing user profile
  ///////////////////////////////////////////////

  useEffect(() => {
    if (isLoggedInWithName) {
      const formFields = getValues();
      formConfig.setExistingProfile(setValue, formFields);
    }
  }, [isLoggedInWithName]);

  ////////////////////////////////////////
  // useEffect
  // - Restore existing form input and bring user to contact details page
  ////////////////////////////////////////

  useEffect(() => {
    const existingFormInput = localStorage.getItem('existingFormInput');

    if (existingFormInput) {
      const parsedFormInput = JSON.parse(existingFormInput);

      if (
        parsedFormInput.formConfig.formName === storyblokWebformComponents.Name
      ) {
        Object.keys(parsedFormInput).forEach(key => {
          setValue(
            key as keyof typeof WebformDefaultFormValues,
            parsedFormInput[key]
          );
        });

        if (isLoggedInWithName) {
          setWebformPage(WebformPage.ContactDetails);
          localStorage.removeItem('existingFormInput');
        }
      }
    }
  }, [isLoggedInWithName]);

  ////////////////////////////////////////
  // useEffect
  // - Set up form schemas
  ////////////////////////////////////////

  useEffect(() => {
    const saveFormSchemas = () => {
      const formPageSchema = generateFormDetailsSchema(formPage[0].PageLayout);

      if (hasContactDetailsPage) {
        const contactDetailsSchema = generateFormDetailsSchema(
          contactDetailsPage[0].PageLayout
        );

        setMergedFormSchema(
          z.discriminatedUnion('formType', [
            z.object({
              formType: z.literal(WebformTypeEnum.FormDetails),
              formDetails: formPageSchema,
            }),
            z.object({
              formType: z.literal(WebformTypeEnum.ContactDetails),
              contactDetails: contactDetailsSchema,
            }),
          ])
        );
      } else {
        setMergedFormSchema(
          z.object({
            formType: z.literal(WebformTypeEnum.FormDetails),
            formDetails: formPageSchema,
          })
        );
      }
    };

    saveFormSchemas();
  }, []);

  ////////////////////////////////////////
  // Stepper
  ////////////////////////////////////////

  const steps = [{ title: formPage[0].PageTitle }];

  if (hasContactDetailsPage) {
    steps.push({ title: contactDetailsPage[0].PageTitle });
  }

  if (hasSummaryPage) {
    steps.push({ title: summaryPage[0].PageTitle });
  }

  const { activeStep, setActiveStep } = useSteps({
    index: 1,
    count: steps.length,
  });

  const goBackToSelectedStep = (selectedStepIndex: number) => {
    setActiveStep(selectedStepIndex);

    switch (selectedStepIndex) {
      case 0:
        setWebformPage(WebformPage.FormDetails);
        break;
      case 1:
        if (hasContactDetailsPage) {
          setWebformPage(WebformPage.ContactDetails);
        }
        break;
    }
  };

  ///////////////////////////////////////////////
  // useEffect
  // - Set the correct form schema based on the webformPage that is displayed
  // - Set Active Step
  ///////////////////////////////////////////////

  useEffect(() => {
    const setFormTypeBasedOnWebformPage = (
      webformPageToDisplay: WebformPage
    ) => {
      switch (webformPageToDisplay) {
        case WebformPage.FormDetails:
          setActiveStep(WebformPage.FormDetails);
          return WebformTypeEnum.FormDetails;

        case WebformPage.ContactDetails:
          setActiveStep(WebformPage.ContactDetails);
          return WebformTypeEnum.ContactDetails;

        case WebformPage.SummaryPage:
          setActiveStep(WebformPage.SummaryPage);
          return WebformTypeEnum.FormDetails;

        case WebformPage.SuccessPage:
          setActiveStep(WebformPage.SuccessPage);
          return WebformTypeEnum.FormDetails;

        default:
          return WebformTypeEnum.FormDetails;
      }
    };

    setValue('formType', setFormTypeBasedOnWebformPage(webformPage));
  }, [webformPage]);

  ////////////////////////////////////////////
  // Handle Functions (Back, Next, Submit)
  ////////////////////////////////////////////

  ////////////////////////////////////////
  // 1) Back
  ////////////////////////////////////////

  const handleBack = () => {
    setShowErrorToast(false);

    switch (webformPage) {
      case WebformPage.ContactDetails:
        setWebformPage(WebformPage.FormDetails);
        break;
      case WebformPage.SummaryPage:
        if (hasContactDetailsPage) {
          setWebformPage(WebformPage.ContactDetails);
        } else {
          setWebformPage(WebformPage.FormDetails);
        }
        break;
    }

    scrollToWebformHeader();
  };

  ////////////////////////////////////////
  // 2) Next
  ////////////////////////////////////////

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Return to Home Page
  const returnToHomePage = async () => {
    // https://nextjs.org/docs/app/api-reference/functions/use-router#router-events
    // https://developer.mozilla.org/en-US/docs/Web/API/Window/location
    // - router.replace() - does not reload the page
    // - window.location.replace() - reloads the page

    // use window.location.replace instead of window.location.href to prevent users from clicking on back button
    if (isNewDesign) {
      window.location.replace(`${basePath}/gov-support`);
    } else {
      window.location.replace(`${basePath}`);
    }
  };

  const handleNext = useCallback(async () => {
    // If error with network connection, show error toast and prevent user from proceeding until connection is restored
    if (!navigator.onLine) {
      setShowErrorToast(true);
      return;
    } else {
      setShowErrorToast(false);
    }

    if (webformPage === WebformPage.SuccessPage) {
      await returnToHomePage(); // Wait for navigation to home page
      await delay(3000); // Add a 3-second delay to ensure user is at the home page before the form returns to the first page
      setWebformPage(WebformPage.FormDetails);
      return;
    }

    const currentPageHasNoErrors = await trigger(); // true / false

    if (currentPageHasNoErrors) {
      const loginRequiredToSubmitWebform =
        storyblokWebformComponents.LoginRequired;

      if (loginRequiredToSubmitWebform && !isLoggedInWithName) {
        setShowLoginModal(true);
        localStorage.setItem('existingFormInput', JSON.stringify(getValues()));
        localStorage.setItem('showWebformAfterLogin', 'true');
        return;
      }

      const nextPage =
        webformPage === WebformPage.FormDetails && hasContactDetailsPage
          ? WebformPage.ContactDetails
          : WebformPage.SummaryPage;

      setWebformPage(nextPage);
      scrollToWebformHeader();
    }
  }, [webformPage, trigger, setWebformPage, isLoggedInWithName, getValues]);

  const nextButtonLabel = () => {
    if (isSubmitPage) {
      return Constants.Webform.Button.Submit;
    } else if (isSuccessPage) {
      return Constants.Webform.Button.ReturnToHomePage;
    } else {
      return Constants.Webform.Button.Next;
    }
  };

  ////////////////////////////////////////
  // 3) Submit
  ////////////////////////////////////////

  const handleFormSubmit = async (recaptchaToken = '') => {
    setShowErrorToast(false);

    try {
      const formData = getValues();

      const payload = {
        ...formData,
        recaptchaToken,
      };

      const response = await sendWebform(payload);

      if (response.status !== 200) {
        setShowErrorToast(true);
        return;
      }

      //////////////////////////////////////
      // Store requestId in Zustand
      //////////////////////////////////////

      const { requestId } = response;
      setRequestId(requestId);

      const { formDetails, contactDetails } = formData;

      // Save Contact Details if checkbox is selected
      if (contactDetails.saveContactDetails) {
        const updatedProfileData = extractUpdatedProfileData(
          formDetails,
          contactDetails
        );

        // Update profile with selected personal details fields if any
        if (Object.keys(updatedProfileData).length > 0) {
          await updateProfile(updatedProfileData);
        }

        // Update user's name if there are changes
        const currentName = userInfo.name ?? '';
        const { firstName, lastName } = contactDetails;

        await updateNameIfChanged(currentName, firstName, lastName);
      }

      setWebformPage(WebformPage.SuccessPage);
      scrollToWebformHeader();

      // Trigger GTM Form Submit Event
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({
        event: 'gtm.formSubmit',
      });

      // Trigger Adobe Launch Form Submit Event
      //   window.digitalData = window.digitalData || {};
      //   window.digitalData.events = window.digitalData.events || {};
      //   window.digitalData.page = window.digitalData.page || {};
      //
      //   window.digitalData.events.eventName = 'formSubmit';
      //   window.digitalData.page.formInfo = {
      //     formType: 'Nav Webform',
      //     formName: storyblokWebformComponents.Name,
      //     formPosition: 'form end',
      //   };
      //
      //   window._satellite?.track(window.digitalData.events.eventName);
    } catch (error) {
      console.error('Error submitting webform: ', error);
      setShowErrorToast(true);
    }
  };

  ////////////////////////////////////////////
  // If there are errors, scroll to the first error field
  ////////////////////////////////////////////

  const scrollToLabelWithError = (label: string) => {
    const elements = document.querySelectorAll<HTMLElement>(
      '.custom-form-field-label'
    );

    // Find and scroll to the element with the matching label
    elements.forEach(element => {
      if (element.textContent?.trim() === label?.trim()) {
        scrollToElementWithOffset(element, 100);
      }
    });
  };

  useEffect(() => {
    const getFirstErrorField = (errorSection: any) =>
      errorSection ? Object.keys(errorSection)[0] : '';

    const scrollToErrorField = (errorField: any) => {
      const errorFieldLabel = errorField?.label;
      if (errorFieldLabel) {
        scrollToLabelWithError(errorFieldLabel);
      }
    };

    if (webformPage === WebformPage.FormDetails) {
      const firstErrorField = getFirstErrorField(errors.formDetails);
      const errorField = formPageFieldNamesAndLabels.find(
        (field: any) => field.name === firstErrorField
      );
      scrollToErrorField(errorField);
    } else if (webformPage === WebformPage.ContactDetails) {
      const firstErrorField = getFirstErrorField(errors.contactDetails);
      const errorField = contactDetailsPageFieldNamesAndLabels.find(
        (field: any) => field.name === firstErrorField
      );
      scrollToErrorField(errorField);
    }
  }, [errors, webformPage]);

  ////////////////////////////////////////
  // Render Page
  ////////////////////////////////////////

  const renderPage = () => {
    switch (webformPage) {
      case WebformPage.FormDetails:
        return (
          <CustomStoryblokForm
            formPageArray={formPage}
            formPageName={'formDetails'}
            isStandaloneForm={isStandaloneForm}
            steps={steps}
            activeStep={activeStep}
            goBackToSelectedStep={goBackToSelectedStep}
          />
        );
      case WebformPage.ContactDetails:
        return (
          <CustomStoryblokForm
            formPageArray={contactDetailsPage}
            formPageName={'contactDetails'}
            isStandaloneForm={isStandaloneForm}
            steps={steps}
            activeStep={activeStep}
            goBackToSelectedStep={goBackToSelectedStep}
          />
        );
      case WebformPage.SummaryPage:
        return (
          <SummaryPage
            title={summaryPage[0]?.PageTitle}
            description={render(summaryPage[0]?.PageDescriptionCTA)}
            isStandaloneForm={isStandaloneForm}
            steps={steps}
            activeStep={activeStep}
            goBackToSelectedStep={goBackToSelectedStep}
          />
        );
      case WebformPage.SuccessPage:
        return (
          <SuccessPage
            title={successPage[0]?.PageTitle}
            description={render(successPage[0]?.PageDescriptionCTA)}
            isStandaloneForm={isStandaloneForm}
            steps={steps}
            activeStep={activeStep}
            goBackToSelectedStep={goBackToSelectedStep}
          />
        );
      default:
        return null;
    }
  };

  ////////////////////////////////////////
  // FOR DEBUGGING
  ////////////////////////////////////////

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Form Values:', watch());
      console.log('Errors:', errors);
    }
  }, [watch, errors]);

  //////////////////////////////
  // Google reCAPTCHA
  //////////////////////////////

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaErrorMessage, setRecaptchaErrorMessage] = useState('');

  const isDevOrTest =
    envConfig.internal.appEnv === 'development' ||
    envConfig.internal.appEnv === 'test';

  const verifyRecaptchaAndSubmitForm = async () => {
    if (!isSubmitPage) return;

    // Skip reCAPTCHA verification in dev / test environments (for e2e tests)
    if (isDevOrTest) {
      await handleFormSubmit();
      return;
    }

    if (recaptchaRef.current) {
      try {
        const token = (await recaptchaRef.current.executeAsync()) ?? '';
        recaptchaRef.current.reset();
        await handleFormSubmit(token);
      } catch (err) {
        console.error('Failed to execute reCAPTCHA:', err);
        setRecaptchaErrorMessage(
          Constants.LoginModal.OtpInput.Errors.Recaptcha
            .FailedRecaptchaValidation
        );
      }
    } else {
      await handleFormSubmit();
    }
  };

  /////////////////////////////////////////

  return (
    <FormProvider {...webformMethods}>
      <Flex className={'webform-outer-container'}>
        <form
          className={'webform-inner-container'}
          onSubmit={handleSubmit(verifyRecaptchaAndSubmitForm)}
        >
          <Flex className={'webform-body'}>{renderPage()}</Flex>

          {showErrorToast && (
            <CustomToast
              variant={'error'}
              description={Constants.ErrorMessage}
            />
          )}

          <Flex className={'webform-footer'}>
            {(isContactDetailsPage || isSummaryPage) && (
              <CustomButton
                type={'outline'}
                leftIcon={'chevronLeft'}
                onClick={handleBack}
              >
                {Constants.Webform.Button.Back}
              </CustomButton>
            )}

            <CustomButton
              type={'solid'}
              rightIcon={!isSubmitPage ? 'chevronRight' : ''}
              isLoading={isSubmitting}
              onClick={
                isSubmitPage
                  ? handleSubmit(verifyRecaptchaAndSubmitForm)
                  : handleNext
              }
            >
              {nextButtonLabel()}
            </CustomButton>
          </Flex>
        </form>
      </Flex>

      {/*Recaptcha (v2 - Invisible)*/}
      <Flex className={'webform-recaptcha'}>
        <ReCAPTCHA
          sitekey={process.env.NEXT_PUBLIC_WEBFORM_RECAPTCHA_SITE_KEY ?? ''}
          ref={recaptchaRef}
          className={'recaptcha-component'}
          size={'invisible'}
          badge={'inline'}
        />
        <CustomErrorMessage errorMessage={recaptchaErrorMessage} />
      </Flex>
    </FormProvider>
  );
};

export default Webform;
